import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, FileSpreadsheet, BadgeCheck, ArrowRight } from "lucide-react";
import { API, WA_LINK, WA_NUMBER, scrollToSection } from "@/lib/api";

const HERO_IMG =
  "https://images.unsplash.com/photo-1646424857576-2a66db82a65c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwzfHxtYXNqaWQlMjBuYWJhd2klMjBtYWRpbmFoJTIwcmF3ZGFoJTIwdW1yYWh8ZW58MHx8fHwxNzkwOTI1OTYwfDA&ixlib=rb-4.1.0&q=85";

const LINES = [
  { text: "JANGAN BIARKAN", gradient: false },
  { text: "JAMAAH ANDA", gradient: false },
  { text: "TIDAK MASUK KE", gradient: true },
  { text: "RAUDAH", gradient: true },
];

const PermitCard = () => (
  <motion.div
    animate={{ y: [0, -14, 0], rotate: [0, 1.2, 0] }}
    transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
    className="glass rounded-2xl p-5 w-64 sm:w-72 shadow-2xl shadow-sky-950/50"
    data-testid="hero-permit-card"
  >
    <div className="flex items-center justify-between">
      <span className="font-mono2 text-[10px] tracking-[0.25em] text-sky-400">INDORAWDAH</span>
      <BadgeCheck size={16} className="text-emerald-400" />
    </div>
    <p className="font-display font-bold text-white text-lg mt-2">PERMIT RAWDAH</p>
    <div className="border-t border-dashed border-white/15 my-3" />
    <div className="space-y-1.5 font-mono2 text-[11px] text-slate-300">
      <p>NAMA&nbsp;&nbsp;: JAMAAH CONTOH</p>
      <p>VISA&nbsp;&nbsp;&nbsp;: V987654321</p>
      <p>SLOT&nbsp;&nbsp;&nbsp;: 21:40 — 22:00</p>
    </div>
    <div className="flex items-end gap-[3px] h-8 mt-4" aria-hidden="true">
      {[3, 1, 2, 1, 4, 2, 1, 3, 1, 2, 4, 1, 2, 3, 1, 2, 1, 4, 2, 1, 3, 2].map((w, i) => (
        <div key={i} className="bg-slate-200/90" style={{ width: `${w}px`, height: "100%" }} />
      ))}
    </div>
    <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 border border-emerald-400/30 px-3 py-1">
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-glow" />
      <span className="text-[10px] font-mono2 tracking-widest text-emerald-300">TERVERIFIKASI 100%</span>
    </div>
  </motion.div>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const cardY = useTransform(scrollYProgress, [0, 1], [0, -110]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="beranda" ref={ref} className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <img src={HERO_IMG} alt="Kubah Hijau Masjid Nabawi, Madinah" className="w-full h-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#030712]/80 via-[#0A0F1D]/85 to-[#030712]" />
      <div className="absolute inset-0 bg-grid opacity-60" />
      <div className="absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full bg-sky-600/20 blur-[140px]" />
      <div className="absolute bottom-0 right-0 w-[420px] h-[420px] rounded-full bg-emerald-600/15 blur-[140px]" />

      <motion.div style={{ opacity: fade }} className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-24 w-full">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="font-mono2 text-xs uppercase tracking-[0.3em] text-sky-400 mb-6"
              data-testid="hero-eyebrow"
            >
              Jasa Booking Permit Rawdah — B2B Travel Umroh
            </motion.p>

            <h1 className="font-display font-extrabold tracking-tight leading-[0.98] text-4xl sm:text-5xl lg:text-6xl" data-testid="hero-headline">
              {LINES.map((line, i) => (
                <span key={line.text} className="block overflow-hidden pb-1">
                  <motion.span
                    className={`block ${line.gradient ? "text-gradient-silver" : "text-white"}`}
                    initial={{ y: "112%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.9, delay: 0.15 + i * 0.13, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {line.text}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.75 }}
              className="mt-6 text-base md:text-lg text-slate-300 leading-relaxed max-w-xl"
              data-testid="hero-subheadline"
            >
              Booking jadwal permit Rawdah <span className="text-white font-semibold">100% aman menggunakan data visa resmi</span> jamaah
              Anda. Kirim data visa — kami bookingkan jadwal permitnya sampai terbit.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.9 }}
              className="mt-8 flex flex-wrap items-center gap-4"
            >
              <button
                onClick={() => scrollToSection("#kontak")}
                data-testid="hero-booking-button"
                className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold text-white bg-gradient-to-br from-sky-500 to-sky-700 hover:from-sky-400 hover:to-sky-600 shadow-lg shadow-sky-900/50 transition-all duration-300 hover:-translate-y-0.5"
              >
                Booking Permit Sekarang
                <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <a
                href={`${API}/template.xlsx`}
                data-testid="hero-download-template-button"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold text-slate-100 border border-white/20 hover:border-emerald-400/60 hover:text-emerald-300 transition-colors duration-300"
              >
                <FileSpreadsheet size={17} />
                Download Template Excel
              </a>
            </motion.div>

            <motion.a
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 1.1 }}
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-admin-badge"
              className="mt-8 inline-flex items-center gap-2.5 rounded-full glass px-5 py-2.5 hover:border-sky-400/40 transition-colors duration-300"
            >
              <Phone size={14} className="text-emerald-400" />
              <span className="text-sm text-slate-300">Admin Fast Response:</span>
              <span className="text-sm font-mono2 font-semibold text-white">{WA_NUMBER}</span>
            </motion.a>
          </div>

          <motion.div style={{ y: cardY }} className="lg:col-span-5 flex justify-center lg:justify-end">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, rotate: -4 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ duration: 1, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
            >
              <PermitCard />
            </motion.div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};
